import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Star, Sparkles, Crown, Palette, Gem } from 'lucide-react';
import { DjaySpecimen, DjayVariant, DjayAccessory, DjayTier, DjayTreatment, MascotRarity, TreatmentUnlockInfo } from '../../mascot';
import { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
import { AccessoryUnlockInfo, TierUnlockInfo, ACCESSORY_CATEGORY_LABELS, AccessoryCategory } from '../../mascot/useDjayCustomization';
import { RADIUS } from '../../config/designTokens';
type CustomizationTab = 'looks' | 'accessories' | 'tiers' | 'finishes';
/**
 * CollectionLocker — the learner-facing Djay locker/customizer.
 *
 * Renamed from `DjayShowcase` (MASCOT_COLLECTION_DDD P2a) to remove the
 * duplicate-symbol collision with the admin-only `mascot/DjayShowcase.tsx`
 * QA configurator. This is the look-PICKER shown on the Collection page; the
 * admin one inspects the raw render-primitive matrix.
 */
interface CollectionLockerProps {
  variants: VariantUnlockInfo[];
  selectedVariant: DjayVariant;
  onSelectVariant: (variantId: DjayVariant) => void;
  accessories: AccessoryUnlockInfo[];
  selectedAccessory: DjayAccessory;
  onSelectAccessory: (accessoryId: DjayAccessory) => void;
  tiers: TierUnlockInfo[];
  selectedTier: DjayTier;
  onSelectTier: (tierId: DjayTier) => void;
  treatments: TreatmentUnlockInfo[];
  selectedTreatment: DjayTreatment;
  onSelectTreatment: (treatmentId: DjayTreatment) => void;
  focusedVariant: string | null;
  rarityStyles: Record<MascotRarity, {
    border: string;
    bg: string;
    text: string;
    glow: string;
    label: string;
  }>;
}
export function CollectionLocker({
  variants,
  selectedVariant,
  onSelectVariant,
  accessories,
  selectedAccessory,
  onSelectAccessory,
  tiers,
  selectedTier,
  onSelectTier,
  treatments,
  selectedTreatment,
  onSelectTreatment,
  focusedVariant,
  rarityStyles
}: CollectionLockerProps) {
  const [activeTab, setActiveTab] = useState<CustomizationTab>('looks');
  const activeVariantInfo = variants.find(v => v.id === selectedVariant);
  const activeAccessoryInfo = accessories.find(a => a.id === selectedAccessory);
  const activeTierInfo = tiers.find(t => t.id === selectedTier);
  const activeTreatmentInfo = treatments.find(t => t.id === selectedTreatment);
  return <div className={`bg-white p-6 sm:p-8 ${RADIUS.card} border-2 border-slate-100 shadow-sm flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start`} data-id="element-1216" data-mp="3ruijmxpamxqt">
      
      {/* Left: Hero Djay */}
      <div className="flex flex-col items-center gap-4 w-full lg:w-1/3" data-id="element-1217" data-mp="2z7oauy1u9mt6">
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 flex-shrink-0 flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50 rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-id="element-1218" data-mp="345zir38kpn8r">
          <motion.div key={`${selectedVariant}-${selectedAccessory}-${selectedTier}-${selectedTreatment}`} initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.5,
          duration: 0.6
        }} className="transform scale-[1.8] mt-8" data-id="element-1219" data-mp="jfib1n5nqhth">
            
            <DjaySpecimen look={{
            variant: selectedVariant,
            accessory: selectedAccessory,
            tier: selectedTier,
            treatment: selectedTreatment
          }} size="xl" emotion="excited" animate={true} data-id="element-1220" data-mp="1z59gmisgtcqd" />
            
          </motion.div>
        </div>
        <div className="text-center" data-id="element-1221" data-mp="1l69rzb4ycxu7">
          <h3 className="text-xl font-black text-slate-800" data-id="element-1222" data-mp="1g886kjk20mfk">
            {activeVariantInfo?.name || 'Jouw Djay'}
          </h3>
          <div className="flex flex-col items-center gap-1 mt-1" data-id="element-1223" data-mp="1baf3hze1s8ji">
            {activeAccessoryInfo && activeAccessoryInfo.id !== 'none' && <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1224" data-mp="3alpysjvem7gx">
                + {activeAccessoryInfo.name}
              </span>}
            {activeTierInfo && activeTierInfo.id !== 'rookie' && <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1225" data-mp="2385nvxmohgdf">
                <Crown className="w-3 h-3" data-id="element-1226" data-mp="1tcc8m6hkr8ry" /> {activeTierInfo.name}
              </span>}
            {activeTreatmentInfo && activeTreatmentInfo.id !== 'none' && <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1227" data-mp="1y92mqo9tjuaq">
                <Gem className="w-3 h-3" data-id="element-1228" data-mp="1ocpvdx1qxsj5" /> {activeTreatmentInfo.name}
              </span>}
          </div>
        </div>
      </div>

      {/* Right: Customizer */}
      <div className="flex-1 w-full flex flex-col gap-4" data-id="element-1229" data-mp="1an5vcyzjv9dj">
        {/* Tabs */}
        <div className="flex p-1 bg-slate-100 rounded-xl" data-id="element-1230" data-mp="1fl7e1it2d6oh">
          <button onClick={() => setActiveTab('looks')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'looks' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1231" data-mp="10nf0gw72uscx">
            
            <Palette className="w-4 h-4" data-id="element-1232" data-mp="m29g2hmqwk6q" />
            Looks
          </button>
          <button onClick={() => setActiveTab('accessories')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'accessories' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1233" data-mp="i0o1mrukbxuf">
            
            <Sparkles className="w-4 h-4" data-id="element-1234" data-mp="14xhjv2oggsa2" />
            Accessoires
          </button>
          <button onClick={() => setActiveTab('tiers')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'tiers' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1235" data-mp="10x7cbll0ltt3">
            
            <Crown className="w-4 h-4" data-id="element-1236" data-mp="1ooco9pnu41y4" />
            Rang
          </button>
          <button onClick={() => setActiveTab('finishes')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'finishes' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1237" data-mp="1joqc7jjqsrqw">
            
            <Gem className="w-4 h-4" data-id="element-1238" data-mp="20puhard44wpr" />
            Finish
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-1239" data-mp="25gb06qzpdaji">
          {activeTab === 'looks' && <motion.div key="looks" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1240" data-mp="25hm2snhewc91">
            
              <div className="flex items-center justify-between mb-2" data-id="element-1241" data-mp="2jdzhjouk9zjt">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1242" data-mp="2odu6hmus0f28">
                  <Palette className="w-4 h-4 text-amber-500" data-id="element-1243" data-mp="p32xswe0lsp6" />
                  Kies je look
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1244" data-mp="u1xglybzfh9a">
                  {variants.filter(v => v.unlocked).length} /{' '}
                  {variants.length} ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-4" data-id="element-1245" data-mp="cra9o2v34stq">
                {variants.map(card => {
              const isSelected = selectedVariant === card.id;
              const isFocused = focusedVariant === card.id;
              const styles = rarityStyles[card.rarity];
              return <motion.button key={card.id} id={`variant-${card.id}`} whileHover={card.unlocked && !isSelected ? {
                scale: 1.05
              } : {}} whileTap={card.unlocked && !isSelected ? {
                scale: 0.95
              } : {}} onClick={() => {
                if (card.unlocked && !isSelected) onSelectVariant(card.id);
              }} className={`
                        relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all
                        ${card.unlocked ? `${styles.bg} ${styles.border} cursor-pointer` : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 shadow-md' : ''}
                        ${isFocused ? 'ring-4 ring-amber-400 ring-offset-2 animate-pulse' : ''}
                      `} title={card.name} aria-label={card.unlocked ? `Kies ${card.name}` : `Vergrendeld: ${card.name}`} data-id="element-1246" data-mp="10ifp4gx45j3i">
                    
                      {!card.unlocked && <div className="absolute top-2 right-2 z-20" data-id="element-1247" data-mp="sgzw4rumypvd">
                          <div className="w-5 h-5 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1248" data-mp="epbp32z8url9">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1249" data-mp="ipvipwklzd2n" />
                          </div>
                        </div>}

                      <div className={`flex items-center justify-center ${!card.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1250" data-mp="1b80uperugjee">
                      
                        {card.rarity === 'secret' && !card.unlocked ? <span className="text-2xl font-black text-slate-300 block" data-id="element-1251" data-mp="2qzl6xpp9lyja">
                            ???
                          </span> : <DjaySpecimen look={{
                    variant: card.id
                  }} size="xl" emotion={card.unlocked ? isSelected ? 'excited' : 'happy' : 'sleeping'} animate={false} data-id="element-1252" data-mp="35or65do3ror6" />}
                      </div>

                      {card.unlocked && <div className={`absolute bottom-0 left-0 right-0 h-1.5 ${styles.bg.replace('bg-', 'bg-').replace('50', '300')}`} data-id="element-1253" data-mp="2d1wospml15hx" />}
                    </motion.button>;
            })}
              </div>

              {/* Selected/Focused Variant Details */}
              <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1254" data-mp="2hxvkkya8ppsd">
                {variants.map(v => {
              const showDetails = focusedVariant ? v.id === focusedVariant : v.id === selectedVariant;
              if (!showDetails) return null;
              return <motion.div key={v.id} initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex flex-col gap-2" data-id="element-1255" data-mp="3s664y27cn0tg">
                    
                      <div className="flex items-center gap-2" data-id="element-1256" data-mp="3idrywx98n81h">
                        <span className="font-black text-slate-700" data-id="element-1257" data-mp="3dfqcq3gimsls">
                          {v.name}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rarityStyles[v.rarity].bg} ${rarityStyles[v.rarity].text}`} data-id="element-1258" data-mp="38fkl2na8x63p">
                        
                          {rarityStyles[v.rarity].label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-1259" data-mp="2ujqjkpbsxj0m">{v.description}</p>

                      {!v.unlocked && v.progress.max > 0 && <div className="mt-2" data-id="element-1260" data-mp="xn9li6jqc31q">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1261" data-mp="srkl60zvyi4d">
                            <span data-id="element-1262" data-mp="109adi1eww1ql">{v.condition}</span>
                            <span data-id="element-1263" data-mp="1594zbqjty14s">
                              {v.progress.current} / {v.progress.max}
                            </span>
                          </div>
                          <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1264" data-mp="1a1y7ypif5tm5">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${v.progress.current / v.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1265" data-mp="1f111pdsy8det" />
                        
                          </div>
                        </div>}
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'accessories' && <motion.div key="accessories" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1266" data-mp="27n3z5oa8indm">
            
              <div className="flex items-center justify-between mb-2" data-id="element-1267" data-mp="gmc2xcd8iag7">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1268" data-mp="ckqh31iq2ltv">
                  <Sparkles className="w-4 h-4 text-amber-500" data-id="element-1269" data-mp="cimh2uny26yv" />
                  Kies je accessoire
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1270" data-mp="3g1fqcv9ow9n8">
                  {accessories.filter(a => a.unlocked).length} /{' '}
                  {accessories.length} ontgrendeld
                </span>
              </div>

              <div className="flex flex-col gap-8" data-id="element-1271" data-mp="3ozrduof9erbg">
                {/* Geen accessoire optie */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1272" data-mp="31a6s5xrq1fyh">
                  {accessories.filter(a => a.id === 'none').map(acc => {
                const isSelected = selectedAccessory === acc.id;
                return <motion.button key={acc.id} whileHover={!isSelected ? {
                  scale: 1.02
                } : {}} whileTap={!isSelected ? {
                  scale: 0.98
                } : {}} onClick={() => {
                  if (!isSelected) onSelectAccessory(acc.id);
                }} className={`
                          relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                          bg-white border-slate-200 cursor-pointer hover:border-amber-300
                          ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                        `} data-id="element-1273" data-mp="2rx7qnkj0ihxi">
                      
                          <div className="h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-slate-50" data-id="element-1274" data-mp="1kjmpobq205w8">
                            <span className="text-5xl" data-id="element-1275" data-mp="1pkk7gn75ixx7">🚫</span>
                          </div>
                          <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1276" data-mp="25ror7vpokkan">
                            {acc.name}
                          </span>
                        </motion.button>;
              })}
                </div>

                {/* Groepeer per categorie */}
                {(Object.keys(ACCESSORY_CATEGORY_LABELS) as AccessoryCategory[]).map(category => {
              const categoryAccessories = accessories.filter(a => a.category === category);
              if (categoryAccessories.length === 0) return null;
              return <div key={category} className="space-y-3" data-id="element-1277" data-mp="2eqtpd0xbybma">
                      <h5 className="font-bold text-slate-400 uppercase tracking-wider text-xs border-b-2 border-slate-100 pb-2" data-id="element-1278" data-mp="2ep8x5hyhn4rl">
                        {ACCESSORY_CATEGORY_LABELS[category]}
                      </h5>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1279" data-mp="139k9sphbo3q9">
                        {categoryAccessories.map(acc => {
                    const isSelected = selectedAccessory === acc.id;
                    return <motion.button key={acc.id} whileHover={acc.unlocked && !isSelected ? {
                      scale: 1.02
                    } : {}} whileTap={acc.unlocked && !isSelected ? {
                      scale: 0.98
                    } : {}} onClick={() => {
                      if (acc.unlocked && !isSelected) onSelectAccessory(acc.id);
                    }} className={`
                                relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                                ${acc.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                                ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                              `} data-id="element-1280" data-mp="3ez06i3cf8opu">
                            
                              {!acc.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1281" data-mp="2586zmfyiofob">
                                  <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1282" data-mp="217n2hcdy1c26">
                                    <Lock className="w-3 h-3 text-slate-500" data-id="element-1283" data-mp="2f8pojp4xumd3" />
                                  </div>
                                </div>}

                              <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 ${!acc.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1284" data-mp="2o7kvj3ft40hg">
                              
                                <DjaySpecimen look={{
                          variant: 'default',
                          accessory: acc.id
                        }} size="hero" emotion={isSelected ? 'excited' : 'happy'} animate={false} data-id="element-1285" data-mp="2xx4fkcf9a0dw" />
                              
                              </div>

                              <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1286" data-mp="32yjz201sbyu6">
                                {acc.name}
                              </span>
                              <span className="text-xs font-medium text-slate-500" data-id="element-1287" data-mp="3qit0ld1q4cc8">
                                {acc.condition}
                              </span>

                              {!acc.unlocked && acc.progress.max > 0 && <div className="w-full mt-3" data-id="element-1288" data-mp="3hhut2ks2tmow">
                                  <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1289" data-mp="32u00d4e2jibt">
                                    <span data-id="element-1290" data-mp="37m9xb4dlpinl">Voortgang</span>
                                    <span data-id="element-1291" data-mp="2tttsby153ak1">
                                      {acc.progress.current} /{' '}
                                      {acc.progress.max}
                                    </span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1292" data-mp="2jwys9ap7x23n">
                                    <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                            width: 0
                          }} animate={{
                            width: `${acc.progress.current / acc.progress.max * 100}%`
                          }} transition={{
                            duration: 0.5
                          }} data-id="element-1293" data-mp="2ou8s7ka8gw5q" />
                                
                                  </div>
                                </div>}
                            </motion.button>;
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'tiers' && <motion.div key="tiers" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1294" data-mp="2cmdnwmif681k">
            
              <div className="flex items-center justify-between mb-2" data-id="element-1295" data-mp="1xov31v8v4eze">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1296" data-mp="1nl65xc3vt14g">
                  <Crown className="w-4 h-4 text-amber-500" data-id="element-1297" data-mp="1sj7toac95om9" />
                  Kies je rang
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1298" data-mp="1xiavllwxreq0">
                  {tiers.filter(t => t.unlocked).length} / {tiers.length}{' '}
                  ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1299" data-mp="14s2b3nexgi89">
                {tiers.map(tier => {
              const isSelected = selectedTier === tier.id;
              return <motion.button key={tier.id} whileHover={tier.unlocked && !isSelected ? {
                scale: 1.02
              } : {}} whileTap={tier.unlocked && !isSelected ? {
                scale: 0.98
              } : {}} onClick={() => {
                if (tier.unlocked && !isSelected) onSelectTier(tier.id);
              }} className={`
                        relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                        ${tier.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                      `} data-id="element-1300" data-mp="1eqi4jx1fg3kx">
                    
                      {!tier.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1301" data-mp="25j6moet126uf">
                          <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1302" data-mp="25hc1r0hraeqc">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1303" data-mp="2ebi2z7ufgdwo" />
                          </div>
                        </div>}

                      <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-4 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 overflow-hidden ${!tier.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1304" data-mp="1ht2ju9nnyb4h">
                      
                        <DjaySpecimen look={{
                    variant: 'default',
                    tier: tier.id
                  }} size="hero" emotion={tier.unlocked ? isSelected ? 'proud' : 'happy' : 'sleeping'} animate={false} data-id="element-1305" data-mp="19cttck31939m" />
                      
                      </div>

                      <span className="font-black text-slate-800 text-lg mb-1" data-id="element-1306" data-mp="10ehslp8gcp3g">
                        {tier.name}
                      </span>
                      <span className="text-xs font-medium text-slate-500" data-id="element-1307" data-mp="qm3sf0x0vqje">
                        {tier.condition}
                      </span>

                      {!tier.unlocked && tier.progress.max > 0 && <div className="w-full mt-3" data-id="element-1308" data-mp="hmgmc1tclwk0">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1309" data-mp="hjkr4l8vxlz9">
                            <span data-id="element-1310" data-mp="cof3nhzu3z92">Voortgang</span>
                            <span data-id="element-1311" data-mp="7oj0tm2vidv3">
                              {tier.progress.current} / {tier.progress.max}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1312" data-mp="2zhyshxm3mak">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${tier.progress.current / tier.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1313" data-mp="3k7lnk8ja2t68" />
                        
                          </div>
                        </div>}
                    </motion.button>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'finishes' && <motion.div key="finishes" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1314" data-mp="bxtyd9gghuf5">
            
              <div className="flex items-center justify-between mb-2" data-id="element-1315" data-mp="2p4a0bj9g2ze1">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1316" data-mp="2k6gsymwenzc6">
                  <Gem className="w-4 h-4 text-amber-500" data-id="element-1317" data-mp="2yv4myoqls100" />
                  Kies je finish
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1318" data-mp="2tye824qjbbg6">
                  {treatments.filter(t => t.unlocked).length} /{' '}
                  {treatments.length} ontgrendeld
                </span>
              </div>

              <p className="text-xs text-slate-500 -mt-2" data-id="element-1319" data-mp="2f8outb563gzj">
                Finishes zijn glanzende effecten die je verdient door te oefenen
                in het Leerlab. Ze passen op elke look.
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1320" data-mp="3qmasxlt08sx7">
                {treatments.map(treatment => {
              const isSelected = selectedTreatment === treatment.id;
              const styles = rarityStyles[treatment.rarity];
              return <motion.button key={treatment.id} whileHover={treatment.unlocked && !isSelected ? {
                scale: 1.02
              } : {}} whileTap={treatment.unlocked && !isSelected ? {
                scale: 0.98
              } : {}} onClick={() => {
                if (treatment.unlocked && !isSelected) onSelectTreatment(treatment.id);
              }} className={`
                        relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                        ${treatment.unlocked ? `${styles.bg} ${styles.border} cursor-pointer hover:brightness-[0.98]` : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? `ring-4 ring-amber-400 ring-offset-2 shadow-md ${styles.glow}` : ''}
                      `} title={treatment.name} aria-label={treatment.unlocked ? `Kies finish ${treatment.name}` : `Vergrendeld: ${treatment.name}`} data-id="element-1321" data-mp="ps4oz48q9eu6">
                    
                      {!treatment.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1322" data-mp="37xe7jfcauqxg">
                          <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1323" data-mp="380a6n6zvvlnh">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1324" data-mp="2y09hh4fc2bbd" />
                          </div>
                        </div>}

                      <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-white/50 ${!treatment.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1325" data-mp="3vm6xmv68q4k5">
                      
                        <DjaySpecimen look={{
                    variant: selectedVariant,
                    treatment: treatment.id
                  }} size="hero" emotion={isSelected ? 'excited' : 'happy'} animate={treatment.unlocked} data-id="element-1326" data-mp="3lwdt2e4imppe" />
                      
                      </div>

                      <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1327" data-mp="dh3maspokwba">
                        <span className="font-black text-slate-800 text-base" data-id="element-1328" data-mp="27p7ilqqyv9jc">
                          {treatment.name}
                        </span>
                        {treatment.id !== 'none' && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${styles.bg} ${styles.text}`} data-id="element-1329" data-mp="1y07exu1irpyt">
                        
                            {styles.label}
                          </span>}
                      </div>
                      <span className="text-xs font-medium text-slate-500" data-id="element-1330" data-mp="1oxodneddtleo">
                        {treatment.condition}
                      </span>

                      {!treatment.unlocked && treatment.progress.max > 0 && <div className="w-full mt-3" data-id="element-1331" data-mp="1f2mq2myr8re7">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1332" data-mp="1jzmu6sc1nk2o">
                            <span data-id="element-1333" data-mp="39etbsfp9yqkr">Voortgang</span>
                            <span data-id="element-1334" data-mp="39d03kvz0jyan">
                              {treatment.progress.current} /{' '}
                              {treatment.progress.max}{' '}
                              {treatment.progress.label}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1335" data-mp="xtalqnzkh4o6">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${treatment.progress.current / treatment.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1336" data-mp="1ci6wwtwx6xt0" />
                        
                          </div>
                        </div>}
                    </motion.button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}